// La contraseña del chofer (decisión D51). Nadie se registra solo: el administrador crea el usuario con el correo y le
// manda por WhatsApp un enlace de un solo uso (`/clave#t=<token>`, vence en 7 días) para que elija su contraseña. El
// mismo enlace llega con "¿Olvidaste tu contraseña?" (por correo si el servidor lo tiene, vence en 1 hora; si no, se lo
// pide al administrador). Después de elegirla, la app entra sola con el correo y la contraseña nueva.
import { CLAVE_MINIMA } from '@reparto/shared/nucleo';
import { api } from './api';
import { entrarConClave } from './sesion';

/** Solo el largo, sin reglas raras (la corta de la demostración la fija un script y sirve igual para entrar). */
export function problemaDeClave(password: string): string | null {
  if (!password) return 'Escribí tu contraseña nueva';
  if (password.length < CLAVE_MINIMA) return `Tiene que tener ${CLAVE_MINIMA} caracteres o más`;
  if (password.length > 200) return 'Es demasiado larga';
  return null;
}

/** Un correo con forma de correo (lo demás lo dice la API). */
export const correoValido = (email: string): boolean => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());

/** El token del enlace, después de "#t=". Null si no hay o no tiene la forma de un token. */
export function leerTokenClave(hash: string): string | null {
  const t = new URLSearchParams(hash.replace(/^#/, '')).get('t');
  return t && /^[A-Za-z0-9_-]{30,80}$/.test(t) ? t : null;
}

// Se lee una sola vez: React monta dos veces en desarrollo y la dirección ya no lo tiene la segunda.
let tokenLeido: string | null = null;

/** Toma el token de la dirección y lo saca del historial (no queda en "atrás" ni en el historial del navegador). */
export function tomarTokenClave(): string | null {
  if (typeof window === 'undefined') return null;
  const t = leerTokenClave(window.location.hash);
  if (t) {
    tokenLeido = t;
    history.replaceState(history.state, '', `${window.location.pathname}${window.location.search}`);
  }
  return t ?? tokenLeido;
}

/** Ya se usó: un enlace sirve una sola vez. */
export function olvidarTokenClave(): void {
  tokenLeido = null;
}

export interface RespuestaOlvide {
  enviado: true;
  modo: 'correo' | 'pedir';
  /** Solo en la demostración: el enlace que llegaría. */
  enlace_demostracion?: string;
}

/** "¿Olvidaste tu contraseña?": la respuesta es la misma exista o no el correo. */
export const pedirEnlaceClave = (email: string) =>
  api<RespuestaOlvide>('/auth/olvide', {
    cuerpo: { email: email.trim().toLowerCase(), app: 'chofer' },
    sinToken: true,
  });

export interface EnlaceClave {
  nombre: string;
  email: string;
  app: 'panel' | 'chofer';
  /** Todavía no tenía contraseña (es el enlace de bienvenida). */
  nueva: boolean;
}

/** A quién es el enlace, sin gastarlo. 401 `ENLACE_INVALIDO` si ya se usó o venció. */
export const revisarEnlaceClave = (token: string) =>
  api<EnlaceClave>('/auth/clave/revisar', { cuerpo: { token }, sinToken: true });

/**
 * Fija la contraseña con el enlace (la API cierra las otras sesiones del usuario) y entra con ella. Si la contraseña
 * quedó pero no pudo entrar (sin señal, o el rol no usa esta app), el error dice `guardada`.
 */
export async function elegirClave(token: string, password: string) {
  const r = await api<{ ok: true; email: string; app: 'panel' | 'chofer' }>('/auth/clave', {
    cuerpo: { token, password },
    sinToken: true,
  });
  olvidarTokenClave();
  try {
    return await entrarConClave(r.email, password);
  } catch (e) {
    throw new ClaveGuardada(r.email, e);
  }
}

/** La contraseña nueva quedó guardada, pero entrar falló (`causa`): se entra después desde /acceso. */
export class ClaveGuardada extends Error {
  constructor(
    readonly email: string,
    readonly causa: unknown,
  ) {
    super('La contraseña quedó guardada');
  }
}

/** Arma la dirección a la que lleva el enlace de demostración dentro de la app (o null si es de otro lado). */
export function rutaDelEnlace(url: string, origen = window.location.origin): string | null {
  try {
    const u = new URL(url);
    return u.origin === origen && u.pathname === '/clave' ? `${u.pathname}${u.hash}` : null;
  } catch {
    return null;
  }
}
